/**
 * 微信二维码登录弹窗组件
 * 用于桌面端显示微信扫码登录二维码
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { RefreshCw, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type {
  WechatLoginConfig,
  WechatLoginError,
  SdkStatus,
} from "@/hooks/useWechatLogin";

export type QRCodeStatus = "loading" | "ready" | "error" | "timeout";

export interface WechatQRCodeDialogProps {
  /** 弹窗是否打开 */
  open: boolean;
  /** 弹窗状态变化回调 */
  onOpenChange: (open: boolean) => void;
  /** 微信登录配置 */
  config: WechatLoginConfig;
  /** SDK 加载状态 */
  sdkStatus: SdkStatus;
  /** 预加载 SDK */
  preloadSdk: () => Promise<void>;
  /** 刷新二维码回调 */
  onRefresh: () => void;
  /** 错误回调 */
  onError?: (error: WechatLoginError) => void;
}

const QR_CODE_TIMEOUT = 10000; // 10 秒超时

export default function WechatQRCodeDialog({
  open,
  onOpenChange,
  config,
  sdkStatus,
  preloadSdk,
  onRefresh,
  onError,
}: WechatQRCodeDialogProps) {
  const { t } = useTranslation("login");
  const [qrStatus, setQrStatus] = useState<QRCodeStatus>("loading");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 清理超时定时器
  const clearQrTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // 渲染二维码
  const renderQRCode = useCallback(async () => {
    if (!open || !containerRef.current) return;

    setQrStatus("loading");
    clearQrTimeout();

    // 确保 SDK 已加载
    if (sdkStatus !== "loaded") {
      try {
        await preloadSdk();
      } catch {
        setQrStatus("error");
        return;
      }
    }

    // 再次检查 SDK 是否可用（preloadSdk 后）
    if (!window.WxLogin) {
      setQrStatus("error");
      onError?.({
        type: "SDK_LOAD_FAILED",
        message: t("domestic.wechatUnavailable", "微信登录脚本未加载"),
      });
      return;
    }

    const container = containerRef.current;

    // 清空容器
    container.innerHTML = "";

    // 设置超时
    timeoutRef.current = setTimeout(() => {
      setQrStatus("timeout");
      onError?.({
        type: "TIMEOUT",
        message: t("domestic.wechatTimeout", "二维码加载超时"),
      });
    }, QR_CODE_TIMEOUT);

    try {
      // 创建二维码
      new window.WxLogin({
        id: container.id,
        appid: config.appId,
        scope: config.scope,
        redirect_uri: encodeURIComponent(config.redirectUri),
        state: config.state,
        self_redirect: false,
        style: "black",
      });

      // 监听 iframe 加载完成
      let checkCount = 0;
      const maxChecks = 50; // 最多检查 5 秒
      const checkIframe = setInterval(() => {
        checkCount++;
        const iframe = container.querySelector("iframe");
        if (iframe) {
          clearInterval(checkIframe);
          clearQrTimeout();
          setQrStatus("ready");
        } else if (checkCount >= maxChecks) {
          clearInterval(checkIframe);
          clearQrTimeout();
          setQrStatus("ready");
        }
      }, 100);
    } catch (err) {
      console.error("[WechatQRCode] 二维码生成失败", err);
      clearQrTimeout();
      setQrStatus("error");
      onError?.({
        type: "SDK_LOAD_FAILED",
        message: t("domestic.wechatUnavailable", "二维码生成失败"),
      });
    }
  }, [
    open,
    config.appId,
    config.redirectUri,
    config.scope,
    config.state,
    sdkStatus,
    preloadSdk,
    onError,
    clearQrTimeout,
    t,
  ]);

  // 弹窗打开时渲染二维码
  useEffect(() => {
    if (open) {
      // 延迟一帧确保 DOM 已渲染
      requestAnimationFrame(() => {
        renderQRCode();
      });
    } else {
      clearQrTimeout();
      setQrStatus("loading");
    }

    return () => {
      clearQrTimeout();
    };
  }, [open, config.state, renderQRCode, clearQrTimeout]);

  // 处理刷新
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    onRefresh();
    // 等待 state 更新后重新渲染
    setTimeout(async () => {
      await renderQRCode();
      setIsRefreshing(false);
    }, 100);
  }, [onRefresh, renderQRCode]);

  // 处理重试
  const handleRetry = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await preloadSdk();
      await renderQRCode();
    } finally {
      setIsRefreshing(false);
    }
  }, [preloadSdk, renderQRCode]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        aria-label={t("domestic.wechatLogin", "微信扫码登录")}
      >
        <VisuallyHidden>
          <DialogTitle>{t("domestic.wechatLogin", "微信扫码登录")}</DialogTitle>
        </VisuallyHidden>
        <div className="flex flex-col items-center justify-center py-4">
          {/* 二维码容器 */}
          <div
            className="relative min-h-[300px] min-w-[300px] flex items-center justify-center"
            role="img"
            aria-label={t("domestic.wechatLogin", "微信登录二维码")}
          >
            {/* 加载状态 */}
            {(qrStatus === "loading" || sdkStatus === "loading") && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center bg-background/80 z-10"
                aria-busy="true"
                aria-live="polite"
              >
                <Loader2 className="h-8 w-8 animate-spin text-[#07c160] mb-2" />
                <p className="text-sm text-muted-foreground">
                  {t("domestic.wechatLoading", "正在加载微信登录...")}
                </p>
              </div>
            )}

            {/* 错误状态 */}
            {(qrStatus === "error" || sdkStatus === "error") && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center bg-background z-10"
                role="alert"
              >
                <p className="text-sm text-destructive mb-4">
                  {t(
                    "domestic.wechatUnavailable",
                    "微信登录暂时不可用，请稍后重试"
                  )}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetry}
                  disabled={isRefreshing}
                >
                  {isRefreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  {t("common.retry", "重试")}
                </Button>
              </div>
            )}

            {/* 超时状态 */}
            {qrStatus === "timeout" && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center bg-background z-10"
                role="alert"
              >
                <p className="text-sm text-muted-foreground mb-4">
                  {t("domestic.wechatTimeout", "二维码已过期，请点击刷新")}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                >
                  {isRefreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  {t("domestic.wechatRefresh", "刷新二维码")}
                </Button>
              </div>
            )}

            {/* 二维码渲染容器 */}
            <div
              id="wechat-qrcode-container"
              ref={containerRef}
              className="flex items-center justify-center"
            />
          </div>

          {/* 微信品牌标识 */}
          <div className="flex items-center gap-2 mt-4 text-muted-foreground">
            <svg
              className="h-5 w-5 text-[#07c160]"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.269-.03-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z" />
            </svg>
            <span className="text-xs">
              {t("domestic.wechatSecureLogin", "微信安全登录")}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
